export type VideoModelId =
	| "seedance-2.0"
	| "seedance-2.0-fast"
	| "klingai-video-3.0-pro"
	| "klingai-video-3.0-4k"
	| "alibaba-happyhorse-1.0"
	| "grok-imagine-video"
	| "heygen-video-agent";

export type VideoAspectRatio = "16:9" | "9:16" | "1:1" | "4:3" | "3:4";
export type VideoResolution = "480p" | "720p" | "1080p" | "4k";

export type VideoModelDefinition = {
	defaultAspectRatio: VideoAspectRatio;
	defaultDurationSeconds: number;
	defaultResolution: VideoResolution;
	description: string;
	hasNativeAudio?: boolean;
	id: VideoModelId;
	isDefault: boolean;
	label: string;
	pricing: {
		type: "per-second";
		usdPerSecond: number;
	};
	providerModelId: string;
	providerName: string;
	resolutions: VideoResolution[];
	supportedAspectRatios: VideoAspectRatio[];
	supportedDurations: {
		maxSeconds: number;
		minSeconds: number;
	};
	supportsNegativePrompt?: boolean;
};

export const VIDEO_MODELS: VideoModelDefinition[] = [
	{
		defaultAspectRatio: "16:9",
		defaultDurationSeconds: 5,
		defaultResolution: "720p",
		description: "Multimodal video generation with synchronized audio.",
		hasNativeAudio: true,
		id: "seedance-2.0",
		isDefault: true,
		label: "Seedance 2.0",
		pricing: {
			type: "per-second",
			usdPerSecond: 0.16,
		},
		providerModelId: "bytedance:seedance@2.0",
		providerName: "ByteDance",
		resolutions: ["720p", "480p"],
		supportedAspectRatios: ["16:9", "9:16", "1:1", "4:3", "3:4"],
		supportedDurations: {
			maxSeconds: 15,
			minSeconds: 2,
		},
	},
	{
		defaultAspectRatio: "16:9",
		defaultDurationSeconds: 5,
		defaultResolution: "720p",
		description: "Fast multimodal video generation for rapid iteration.",
		hasNativeAudio: true,
		id: "seedance-2.0-fast",
		isDefault: false,
		label: "Seedance 2.0 Fast",
		pricing: {
			type: "per-second",
			usdPerSecond: 0.13,
		},
		providerModelId: "bytedance:seedance@2.0-fast",
		providerName: "ByteDance",
		resolutions: ["720p", "480p"],
		supportedAspectRatios: ["16:9", "9:16", "1:1", "4:3", "3:4"],
		supportedDurations: {
			maxSeconds: 15,
			minSeconds: 2,
		},
	},
	{
		defaultAspectRatio: "16:9",
		defaultDurationSeconds: 5,
		defaultResolution: "1080p",
		description: "High-quality cinematic video generation with native audio.",
		hasNativeAudio: true,
		id: "klingai-video-3.0-pro",
		isDefault: false,
		label: "Kling Video 3.0 Pro",
		pricing: {
			type: "per-second",
			usdPerSecond: 0.168,
		},
		providerModelId: "klingai:kling-video@3-pro",
		providerName: "KlingAI",
		resolutions: ["1080p"],
		supportedAspectRatios: ["16:9", "9:16", "1:1"],
		supportedDurations: {
			maxSeconds: 15,
			minSeconds: 5,
		},
		supportsNegativePrompt: true,
	},
	{
		defaultAspectRatio: "16:9",
		defaultDurationSeconds: 5,
		defaultResolution: "4k",
		description: "4K cinematic video generation with native audio.",
		hasNativeAudio: true,
		id: "klingai-video-3.0-4k",
		isDefault: false,
		label: "Kling Video 3.0 4K",
		pricing: {
			type: "per-second",
			usdPerSecond: 0.42,
		},
		providerModelId: "klingai:kling-video@3-4k",
		providerName: "KlingAI",
		resolutions: ["4k"],
		supportedAspectRatios: ["16:9", "9:16", "1:1"],
		supportedDurations: {
			maxSeconds: 15,
			minSeconds: 3,
		},
		supportsNegativePrompt: true,
	},
	{
		defaultAspectRatio: "16:9",
		defaultDurationSeconds: 5,
		defaultResolution: "1080p",
		description: "Alibaba video generation with 720p and 1080p output.",
		id: "alibaba-happyhorse-1.0",
		isDefault: false,
		label: "HappyHorse 1.0",
		pricing: {
			type: "per-second",
			usdPerSecond: 0.24,
		},
		providerModelId: "alibaba:happyhorse@1.0",
		providerName: "Alibaba",
		resolutions: ["1080p", "720p"],
		supportedAspectRatios: ["16:9", "9:16", "1:1", "4:3", "3:4"],
		supportedDurations: {
			maxSeconds: 15,
			minSeconds: 3,
		},
	},
	{
		defaultAspectRatio: "16:9",
		defaultDurationSeconds: 6,
		defaultResolution: "720p",
		description: "xAI cinematic video with prompt-synchronized audio.",
		hasNativeAudio: true,
		id: "grok-imagine-video",
		isDefault: false,
		label: "Grok Imagine Video",
		pricing: {
			type: "per-second",
			usdPerSecond: 0.07,
		},
		providerModelId: "xai:grok-imagine@video",
		providerName: "xAI",
		resolutions: ["720p", "480p"],
		supportedAspectRatios: ["16:9", "9:16", "1:1"],
		supportedDurations: {
			maxSeconds: 15,
			minSeconds: 1,
		},
	},
	{
		defaultAspectRatio: "16:9",
		defaultDurationSeconds: 30,
		defaultResolution: "720p",
		description: "Prompt-only multi-scene avatar and presenter videos.",
		hasNativeAudio: true,
		id: "heygen-video-agent",
		isDefault: false,
		label: "HeyGen Video Agent",
		pricing: {
			type: "per-second",
			usdPerSecond: 0.04,
		},
		providerModelId: "heygen:video-agent@0",
		providerName: "HeyGen",
		resolutions: ["720p"],
		supportedAspectRatios: ["16:9", "9:16"],
		supportedDurations: {
			maxSeconds: 300,
			minSeconds: 5,
		},
	},
];

export function getDefaultVideoModel() {
	return VIDEO_MODELS.find((model) => model.isDefault) ?? VIDEO_MODELS[0];
}

export function getVideoModel(modelId?: string | null) {
	if (!modelId) return getDefaultVideoModel();
	return VIDEO_MODELS.find((model) => model.id === modelId) ?? null;
}

export function getVideoModelOrThrow(modelId?: string | null) {
	const model = getVideoModel(modelId);
	if (!model) {
		throw new Error("Unsupported video model.");
	}
	return model;
}

export function assertVideoOptionsSupported(
	model: VideoModelDefinition,
	durationSeconds: number,
	aspectRatio: VideoAspectRatio,
) {
	if (!model.supportedAspectRatios.includes(aspectRatio)) {
		throw new Error(
			`${model.label} currently supports these aspect ratios: ${model.supportedAspectRatios.join(", ")}.`,
		);
	}
	if (
		durationSeconds < model.supportedDurations.minSeconds ||
		durationSeconds > model.supportedDurations.maxSeconds
	) {
		throw new Error(
			`${model.label} currently supports durations between ${model.supportedDurations.minSeconds} and ${model.supportedDurations.maxSeconds} seconds.`,
		);
	}
}

export function listVideoModels() {
	return VIDEO_MODELS.map((model) => ({
		defaultAspectRatio: model.defaultAspectRatio,
		defaultDurationSeconds: model.defaultDurationSeconds,
		defaultResolution: model.defaultResolution,
		description: model.description,
		id: model.id,
		isDefault: model.isDefault,
		label: model.label,
		pricing: model.pricing,
		provider: model.providerName,
		providerModelId: model.providerModelId,
		resolutions: model.resolutions,
		supportedAspectRatios: model.supportedAspectRatios,
		supportedDurations: model.supportedDurations,
		type: "video" as const,
	}));
}
